import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Plus, Edit2, Trash2, MapPin, Package, Search, Grid, List, Building, CheckCircle } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Table from '../../components/Table';
import RowActions from '../../components/RowActions';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { apiService } from '../../services/apiService';
import { renderCell, coalesce } from '../../utils/helpers';

const SitesManagement = () => {
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();
  const { confirm } = useConfirm();
  const [sites, setSites] = useState([]);
  const [filteredSites, setFilteredSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    clientId: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterSites();
  }, [searchQuery, sites]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [sitesData, clientsData] = await Promise.all([
        apiService.getSites(),
        apiService.getClients(),
      ]);
      setSites(sitesData);
      setClients(clientsData);
    } catch (error) {
      showError('Erreur lors du chargement');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filterSites = () => {
    if (!searchQuery) {
      setFilteredSites(sites);
      return;
    }
    const query = searchQuery.toLowerCase();
    const filtered = sites.filter(
      (site) =>
        site.name?.toLowerCase().includes(query) ||
        site.clientName?.toLowerCase().includes(query) ||
        site.address?.toLowerCase().includes(query)
    );
    setFilteredSites(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSite) {
        await apiService.updateSite(editingSite.id, formData);
        showSuccess('Site mis à jour avec succès');
      } else {
        await apiService.createSite(formData);
        showSuccess('Site créé avec succès');
      }
      setShowModal(false);
      setEditingSite(null);
      setFormData({ name: '', address: '', clientId: '' });
      loadData();
    } catch (error) {
      showError('Erreur lors de la sauvegarde');
      console.error(error);
    }
  };

  const handleEdit = (site) => {
    setEditingSite(site);
    setFormData({
      name: site.name,
      address: site.address || '',
      clientId: site.clientId,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    // Find the site to get equipment count
    const site = sites.find(s => s.id === id);
    const hasEquipment = site && (site.equipmentCount > 0 || site.equipment_count > 0);
    const equipCount = site?.equipmentCount || site?.equipment_count || 0;
    
    const confirmMessage = hasEquipment
      ? `⚠️ Ce site contient ${equipCount} équipement(s).\n\nSupprimer le site ET tous ses équipements ?`
      : 'Supprimer ce site ?';
    
    const ok = await confirm({
      title: 'Supprimer ce site ?',
      message: confirmMessage,
      confirmText: 'Supprimer',
      variant: 'danger',
    });
    if (!ok) return;

    try {
      const result = await apiService.deleteSite(id, true); // Always force delete
      showSuccess(result.message || 'Site supprimé avec succès');
      loadData();
    } catch (error) {
      showError(error.message || 'Erreur lors de la suppression');
      console.error(error);
    }
  };

  const viewEquipment = (site) => {
    navigate(`/admin/sites/${site.id}/equipment`);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSite(null);
    setFormData({ name: '', address: '', clientId: '' });
  };

  const getEquipmentCount = (site) => site?.equipmentCount ?? site?.equipment_count ?? 0;

  // Calculate stats
  const totalSites = sites.length;
  const totalEquipment = sites.reduce((acc, site) => acc + getEquipmentCount(site), 0);
  const sitesWithEquipment = sites.filter(site => getEquipmentCount(site) > 0).length;
  const uniqueClients = [...new Set(sites.map(s => s.clientId))].length;

  const columns = [
    {
      header: 'Site',
      accessor: 'name',
      width: '24%',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <MapPin className="w-4 h-4 text-red-600" />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900">{renderCell(row.name)}</p>
            <p className="text-xs text-gray-500">ID: {row.id}</p>
          </div>
        </div>
      ),
    },
    {
      header: 'Client',
      accessor: 'clientName',
      width: '18%',
      render: (row) => (
        <span className="text-sm text-gray-900 break-words">{renderCell(row.clientName)}</span>
      ),
    },
    {
      header: 'Adresse',
      accessor: 'address',
      width: '34%',
      render: (row) => (
        <span className="text-sm text-gray-600 block break-words line-clamp-2" title={row.address || ''}>
          {renderCell(row.address)}
        </span>
      ),
    },
    {
      header: 'Équipements',
      accessor: 'equipmentCount',
      width: '12%',
      headerClassName: 'text-center',
      cellClassName: 'text-center',
      render: (row) => (
        <div className="flex items-center justify-center gap-2">
          <Package className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-900">
            {getEquipmentCount(row)}
          </span>
        </div>
      ),
    },
    {
      header: 'Actions',
      accessor: 'actions',
      width: '12%',
      size: 'actions',
      actionCount: 3,
      render: (row) => (
        <RowActions
          onView={() => viewEquipment(row)}
          onEdit={() => handleEdit(row)}
          onDelete={() => handleDelete(row.id)}
        />
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Building2 className="w-8 h-8 text-red-600" />
            Gestion des Sites
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Gérez tous les sites clients
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'table' ? 'primary' : 'outline'}
            size="sm"
            icon={List}
            onClick={() => setViewMode('table')}
          >
            Tableau
          </Button>
          <Button
            variant={viewMode === 'card' ? 'primary' : 'outline'}
            size="sm"
            icon={Grid}
            onClick={() => setViewMode('card')}
          >
            Cartes
          </Button>
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => {
              setEditingSite(null);
              setFormData({ name: '', address: '', clientId: '' });
              setShowModal(true);
            }}
          >
            Ajouter un site
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-red-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-red-500 to-red-600 rounded-lg sm:rounded-xl shadow-lg shadow-red-500/30">
                <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Total Sites</p>
            <p className="text-xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{totalSites}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-blue-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg sm:rounded-xl shadow-lg shadow-blue-500/30">
                <Package className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Équipements</p>
            <p className="text-xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{totalEquipment}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-green-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg sm:rounded-xl shadow-lg shadow-green-500/30">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-green-600 bg-green-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                {totalSites > 0 ? Math.round((sitesWithEquipment / totalSites) * 100) : 0}%
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Avec équipements</p>
            <p className="text-xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{sitesWithEquipment}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-purple-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg sm:rounded-xl shadow-lg shadow-purple-500/30">
                <Building className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Clients</p>
            <p className="text-xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{uniqueClients}</p>
          </div>
        </motion.div>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <Input
              placeholder="Rechercher un site..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={Search}
            />
          </div>
          <Button variant="outline">Filtres</Button>
        </div>
      </Card>

      {/* Sites Table View */}
      {viewMode === 'table' && filteredSites.length > 0 && (
        <Card>
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {filteredSites.length} site(s)
            </h2>
          </div>
          <Table columns={columns} data={filteredSites} />
        </Card>
      )}

      {/* Sites Card View */}
      {viewMode === 'card' && filteredSites.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {filteredSites.map((site, index) => (
            <motion.div
              key={site.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="hover:shadow-lg transition-shadow">
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-lg bg-red-100">
                        <MapPin className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{renderCell(site.name)}</h3>
                        <p className="text-sm text-gray-500">{renderCell(site.clientName)}</p>
                      </div>
                    </div>
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {getEquipmentCount(site)} équip.
                    </span>
                  </div>

                  {/* Info */}
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-700">
                      <MapPin className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
                      {renderCell(site.address)}
                    </div>
                    <div className="flex items-center text-sm text-gray-700">
                      <Package className="w-4 h-4 mr-2 text-gray-400" />
                      {getEquipmentCount(site)} équipement(s)
                    </div>
                  </div>

                  {/* ID */}
                  <div className="text-xs text-gray-500">
                    ID: {site.id}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end pt-2 border-t border-gray-200">
                    <RowActions
                      onView={() => viewEquipment(site)}
                      onEdit={() => handleEdit(site)}
                      onDelete={() => handleDelete(site.id)}
                    />
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {filteredSites.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Aucun site trouvé
            </h3>
            <p className="text-gray-600">
              {searchQuery ? 'Essayez un autre terme de recherche' : 'Commencez par créer un site'}
            </p>
          </div>
        </Card>
      )}

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editingSite ? 'Modifier le site' : 'Ajouter un site'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select
            label="Client"
            name="clientId"
            value={formData.clientId}
            onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
            placeholder="Sélectionner un client"
            options={clients.map((c) => ({
              value: c.id,
              label: coalesce(c.company, c.name) || `Client #${c.id}`,
            }))}
            required
          />
          <Input
            label="Nom du site"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ex: Site principal DOE, Entrepôt..."
            required
          />
          <Input
            label="Adresse"
            name="address"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            placeholder="Ex: 12 rue de la Gare, 75001 Paris"
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={closeModal}>
              Annuler
            </Button>
            <Button type="submit" variant="primary">
              {editingSite ? 'Mettre à jour' : 'Créer'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SitesManagement;
